import { destroyDiInstance, diInject } from '@di/di';
import { VideoSizeMode } from '@domain/video';
import { isAppleBrowser, isWKWebView } from '@studio/utils/ad/browser';
import { isAV1CodecDecodingSupported, isH265HVC1Supported, isHlsSupported, isWebmVp9Supported } from '@studio/utils/ad/browser-feature-support';
import { cloneDeep } from '@studio/utils/clone';
import { getVideoErrorMessage, VideoError } from '@studio/utils/errors';
import { ErrorMessage } from '@studio/utils/errors/error-message.enum';
import { hasParameter, isBase64Mp4Video, isRelativeUrl, parameterToBool, replaceOrigin } from '@studio/utils/url';
import { deepEqual } from '@studio/utils/utils';
import { isValidUrl } from '@studio/utils/validation';
import { isHidden } from '../../nodes/data-node.utils';
import { CreativeAudioToggle, isMediaDefaultMuted, removeCreativeAudioToggleIfUnused, shouldShowCreativeAudioToggle } from '../media/creative-audio-toggle';
import { DEFAULT_VIDEO_STYLES, PLAYBACK_BUTTON_HTML } from './video-renderer.constants';
import { StreamManager } from './video-streamer/stream-manager';
import { getEffectiveVideoAssetDuration, getEffectiveDuration, getNextOffset, getVideoEndTime, getVideoStartTime } from './media-time-calculations';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const FRAME_RATE_TOLERANCE = 1 / 60; // Tolerance for frame rate, so we don't miss the end of the video time
export class VideoRenderer {
    get _isPlaying() {
        return !this._videoDOMElement.paused;
    }
    get _progressTime() {
        return this._animator.time - this._viewElement.time;
    }
    get _playbackRate() {
        return this._settings.playbackRate || 1;
    }
    get _startTime() {
        if (this._progressTime <= 0) {
            return this._settings.startTime;
        }
        if (!this.duration_m) {
            return this._settings.startTime + this._progressTime;
        }
        return getVideoStartTime({
            effectiveDuration: this._effectiveVideoDuration,
            progressTime: this._progressTime,
            settingsStartTime: this._settings.startTime,
            isVideoLooped: this._settings.loop,
            playbackRate: this._playbackRate
        });
    }
    get _canStartPlaying() {
        if (!this._effectiveVideoDuration) {
            return false;
        }
        if (this._progressTime < 0) {
            return false;
        }
        if (this._settings.loop) {
            const isAnimatorOnElement = this._animator.time < this._viewElement.time + this._viewElement.duration;
            return isAnimatorOnElement;
        }
        if (!this._nonLoopVideoEnded &&
            this._effectiveVideoAssetDuration / this._playbackRate > this._viewElement.duration) {
            const remainingVideoDuration = (this._effectiveVideoAssetDuration - this._videoOffset) / this._playbackRate;
            const isAnimatorOnRemainingVideo = this._animator.time < this._viewElement.time + remainingVideoDuration;
            return isAnimatorOnRemainingVideo;
        }
        const isAnimatorOnVideo = this._animator.time < this._viewElement.time + this._effectiveVideoDuration;
        return isAnimatorOnVideo;
    }
    get duration_m() {
        return this._videoDOMElement.duration;
    }
    get videoDOMElement_m() {
        return this._videoDOMElement;
    }
    constructor(_viewElement) {
        this._viewElement = _viewElement;
        this._destroyed = false;
        this._effectiveVideoDuration = 0;
        this._effectiveVideoAssetDuration = 0;
        this._videoOffset = 0;
        this._endTime = 0;
        this._nonLoopVideoEnded = false;
        this._ad = diInject(0 /* Token.AD */);
        this._renderer = diInject(3 /* Token.RENDERER */);
        this._feedStore = diInject(6 /* Token.FEED_STORE */, { optional: true });
        this._animator = diInject(5 /* Token.ANIMATOR */);
        this._creativeConfig = diInject(2 /* Token.CREATIVE_CONFIG */);
        this._onPlaybackButtonClick = (event) => {
            if (this._renderer.isEditorMode) {
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            if (!this._isPlaying) {
                this._userInitiatedPlaybackState = 'play';
                this._play();
            }
            else {
                this._userInitiatedPlaybackState = 'pause';
                this._pause();
            }
        };
        this._onAnimatorPlay = () => {
            var _a;
            this._userInitiatedPlaybackState = undefined;
            if (!this._settings.autoplay) {
                return;
            }
            const animatorTime = ((_a = this._animator) === null || _a === void 0 ? void 0 : _a.time) || 0;
            if (animatorTime >= this._viewElement.time) {
                this._play();
            }
        };
        this._onAnimatorIsPlaying = (isPlaying) => {
            this._creativeAudioToggle.setVisibilityOnPlayback_m(isPlaying);
        };
        this._onAnimationStart = (element) => {
            if (this._dataElement.id !== element.id) {
                return;
            }
            if (!this._settings.autoplay) {
                if (!this._userInitiatedPlaybackState || this._userInitiatedPlaybackState === 'pause') {
                    return;
                }
            }
            if (this._settings.restartWithCreative) {
                this._resetTimings();
            }
            // Play if: restartWithCreative is on, OR video loops, OR video is not finished yet
            if (this._settings.restartWithCreative || this._settings.loop || !this._nonLoopVideoEnded) {
                this._play();
            }
        };
        this._onAnimationLoop = () => {
            if (this._dataElement.feed) {
                this._setVideoSrc();
                this._play();
            }
            if (!this._settings.restartWithCreative &&
                !this._nonLoopVideoEnded &&
                this._creativeConfig.environment !== 0 /* AdEnvironment.LiveAd */) {
                this._setOffsetTimings();
            }
        };
        this._onAnimationEnd = (element) => {
            if (this._dataElement.id !== element.id) {
                return;
            }
            if (!this._settings.restartWithCreative &&
                this._creativeConfig.environment !== 0 /* AdEnvironment.LiveAd */) {
                this._setLoopOffset();
            }
            this._pause();
        };
        this._onAnimatorSeek = () => {
            if (!this._videoDOMElement) {
                return;
            }
            const previousTime = this._videoDOMElement.currentTime;
            if (this._creativeConfig.environment !== 0 /* AdEnvironment.LiveAd */) {
                this._resetTimings();
            }
            if (this._videoDOMElement.currentTime === previousTime) {
                return;
            }
            if (this._creativeConfig.environment !== 5 /* AdEnvironment.VideoGenerator */) {
                return;
            }
            const promise = new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    this._videoDOMElement.removeEventListener('seeked', onSeeked);
                    reject(new Error(`Seek timed out for video element ${this._dataElement.id}`));
                }, 10000);
                const onSeeked = () => {
                    clearTimeout(timeoutId);
                    this._videoDOMElement.removeEventListener('seeked', onSeeked);
                    resolve();
                };
                this._videoDOMElement.addEventListener('seeked', onSeeked);
            });
            this._renderer.preloadingElements.set(this._dataElement.id, promise);
        };
        this._onRendererMute = (muted) => {
            if (this._videoDOMElement) {
                this._videoDOMElement.muted = muted;
            }
            if (this._streamManager) {
                this._streamManager.toggleAudioMute_m(muted);
            }
        };
        this._play = () => {
            if (this._isPlaying) {
                return;
            }
            if (!this._canStartPlaying) {
                return;
            }
            this._videoDOMElement.play();
            this._setPlaybackButtonState();
            if (this._creativeConfig.environment === 0 /* AdEnvironment.LiveAd */) {
                return;
            }
            this._requestId = requestAnimationFrame(this._checkVideoEnd);
        };
        this._pause = () => {
            if (!this._settings.stopWithCreative &&
                this._creativeConfig.environment !== 1 /* AdEnvironment.App */) {
                const continuePlaying = this._settings.loop ||
                    (!this._nonLoopVideoEnded &&
                        this._effectiveVideoAssetDuration / this._playbackRate >
                            this._viewElement.duration);
                if (continuePlaying) {
                    return;
                }
            }
            this._videoDOMElement.pause();
            this._setPlaybackButtonState();
            cancelAnimationFrame(this._requestId);
        };
        this._stop = () => {
            if (this._settings.stopWithCreative) {
                this._pause();
                return;
            }
            if (this._creativeConfig.environment === 2 /* AdEnvironment.Preview */) {
                const duration = this._effectiveVideoAssetDuration;
                const videoCurrentPosition = this._videoDOMElement.currentTime - this._settings.startTime;
                const videoPlayed = videoCurrentPosition % duration;
                // Media seconds converted to wall-clock for the timers below
                const restOfVideo = (duration - videoPlayed) / this._playbackRate;
                if (this._settings.loop) {
                    this._videoDOMElement.play();
                    setTimeout(() => {
                        this._setLoopVideoStartTime();
                        clearInterval(this._previewLoopIntervalId);
                        this._previewLoopIntervalId = setInterval(() => {
                            this._setLoopVideoStartTime();
                        }, (duration / this._playbackRate) * 1000);
                    }, restOfVideo * 1000);
                }
                else if (!this._nonLoopVideoEnded) {
                    setTimeout(() => {
                        this._videoDOMElement.pause();
                    }, restOfVideo * 1000);
                }
            }
        };
        this._onVideoEnd = () => {
            // In a live ad the native `ended` event is the only end signal, and the
            // video is already paused when it fires, so let ended videos through.
            if (!this._isPlaying && !this._videoDOMElement.ended) {
                return;
            }
            if (this._settings.loop) {
                this._setLoopVideoStartTime();
                this._setLoopEndTime();
                this._requestId = requestAnimationFrame(this._checkVideoEnd);
            }
            else {
                this._nonLoopVideoEnded = true;
                this._showVideoLastFrame();
                this._pause();
            }
        };
        this._checkVideoEnd = () => {
            if (this._destroyed) {
                return;
            }
            if (this._creativeConfig.environment === 0 /* AdEnvironment.LiveAd */) {
                return;
            }
            const timePassedOnVideoWithTolerance = this._progressTime + FRAME_RATE_TOLERANCE;
            if (this._endTime && timePassedOnVideoWithTolerance >= this._endTime) {
                this._onVideoEnd();
                return;
            }
            this._requestId = requestAnimationFrame(this._checkVideoEnd);
        };
        /**
         * Adds or removes an audio toggle to the creative, appended in the creative root element
         * @param showAudioToggle Controls whether the audio toggle should be visible or not
         */
        this._onCreativeAudioSettingToggled = (showAudioToggle) => {
            if (!showAudioToggle) {
                this._creativeAudioToggle.detach_m();
                // Toggle back video audio to default behaviour
                this._setMuted(this.videoDOMElement_m);
                return;
            }
            this._creativeAudioToggle.attach_m();
        };
        this._dataElement = _viewElement.__data;
        this._settings = Object.assign(Object.assign({}, this._dataElement.videoSettings), { url: this._getVideoUrl() });
        if (!this._animator) {
            throw new VideoError('Animator not provided.');
        }
        this._animator.on('play', this._onAnimatorPlay);
        this._animator.on('pause', this._pause);
        this._animator.on('stop', this._stop);
        this._animator.on('loop', this._onAnimationLoop);
        this._animator.on('animation_start', this._onAnimationStart);
        this._animator.on('animation_end', this._onAnimationEnd);
        this._animator.on('seek', this._onAnimatorSeek);
        this._animator.on('isPlaying', this._onAnimatorIsPlaying);
        this._renderer.on('creativeAudioToggled', this._onCreativeAudioSettingToggled);
        this._renderer.on('mute', this._onRendererMute);
        this._creativeAudioToggle = new CreativeAudioToggle(this._renderer, this._creativeConfig);
        this._init();
    }
    _init() {
        this._createVideoDOMElement();
        this.updateVideo(true);
        if (shouldShowCreativeAudioToggle(this._creativeConfig)) {
            this._onCreativeAudioSettingToggled(this._renderer.creativeDocument.audio);
        }
    }
    _createVideoDOMElement() {
        var _a, _b, _c, _d;
        const videoRenderer = document.createElement('video-renderer');
        this._shadowRoot = videoRenderer.attachShadow({ mode: 'open' });
        videoRenderer.style.width = '100%';
        videoRenderer.style.height = '100%';
        videoRenderer.style.display = 'block';
        const video = document.createElement('video');
        video.preload = 'auto';
        this._setMuted(video);
        video.setAttribute('playsinline', '');
        video.style.pointerEvents = 'none';
        video.disableRemotePlayback = true; // Required for Apple to play streaming with ManagedMediaSource
        const posterUrl = (_a = this._dataElement.videoAsset) === null || _a === void 0 ? void 0 : _a.posterUrl;
        if (posterUrl) {
            video.poster = posterUrl;
        }
        this._videoDOMElement = video;
        this._preparePreloading();
        this._setVideoStyles();
        this._shadowRoot.appendChild(this._videoDOMElement);
        if (isWKWebView &&
            this._creativeConfig.environment === 1 /* AdEnvironment.App */ &&
            !this._isEffectivelyMasked()) {
            // ios WKWebView BUG: it can not play video if mask is not applied on it, so we need to append it directly to the root element
            (_b = this._viewElement.__rootElement) === null || _b === void 0 ? void 0 : _b.appendChild(videoRenderer);
        }
        else {
            const foreignObject = (_c = this._viewElement.__rootElement) === null || _c === void 0 ? void 0 : _c.querySelector('foreignObject');
            (_d = foreignObject === null || foreignObject === void 0 ? void 0 : foreignObject.firstElementChild) === null || _d === void 0 ? void 0 : _d.appendChild(videoRenderer);
        }
        this._createPlaybackButtons();
    }
    _isEffectivelyMasked() {
        var _a;
        const maskElementId = (_a = this._dataElement.masking) === null || _a === void 0 ? void 0 : _a.elementId;
        if (maskElementId === undefined) {
            return false;
        }
        const maskNode = this._renderer.creativeDocument.findNodeById_m(maskElementId);
        return !maskNode || !isHidden(maskNode);
    }
    _isStreamingEnabledInUrlParams() {
        if (hasParameter(this._ad.parameters, 'streaming')) {
            return !!parameterToBool(this._ad.parameters.streaming);
        }
        return true;
    }
    _setVideoStyles() {
        const style = document.createElement('style');
        style.textContent = DEFAULT_VIDEO_STYLES;
        this._shadowRoot.appendChild(style);
    }
    _createPlaybackButtons() {
        this._videoPlaybackButtons = document.createElement('div');
        this._videoPlaybackButtons.className = 'playbackButton';
        this._videoPlaybackButtons.style.position = 'absolute';
        this._videoPlaybackButtons.style.display = 'none';
        this._videoPlaybackButtons.style.top = '0px';
        this._videoPlaybackButtons.style.left = '0px';
        this._videoPlaybackButtons.style.width = '100%';
        this._videoPlaybackButtons.style.height = '100%';
        this._videoPlaybackButtons.style.objectFit = 'cover';
        this._videoPlaybackButtons.style.objectPosition = 'center center';
        this._videoPlaybackButtons.addEventListener('click', this._onPlaybackButtonClick);
        this._videoPlaybackButtons.innerHTML = PLAYBACK_BUTTON_HTML;
        this._shadowRoot.appendChild(this._videoPlaybackButtons);
    }
    _preparePreloading() {
        let videoPromise;
        const videoLoadPromise = new Promise((resolve, reject) => {
            videoPromise = {
                resolve,
                reject
            };
        });
        this._renderer.preloadingElements.set(this._dataElement.id, videoLoadPromise);
        // HLS on Safari IOS requires the loadedmetadata event to be triggered before the video can be played
        // https://stackoverflow.com/questions/50051639/javascript-html5-video-event-canplay-not-firing-on-safari
        const eventType = this._streamingMode === 'hls' ? 'loadedmetadata' : 'canplaythrough';
        const onVideoLoaded = () => {
            this._setEffectiveVideoDuration();
            this._resetTimings();
            videoPromise.resolve();
            this._videoDOMElement.removeEventListener(eventType, onVideoLoaded);
        };
        this._videoDOMElement.addEventListener(eventType, onVideoLoaded);
        const onVideoError = async () => {
            var _a, _b;
            const videoError = new VideoError(getVideoErrorMessage((_a = this._videoDOMElement.error) === null || _a === void 0 ? void 0 : _a.code), {
                src: this._videoDOMElement.src,
                code: (_b = this._videoDOMElement.error) === null || _b === void 0 ? void 0 : _b.code
            });
            videoPromise.reject(videoError);
            this._videoDOMElement.removeEventListener('error', onVideoError);
            await this.destroy();
        };
        this._videoDOMElement.addEventListener('error', onVideoError);
        this._videoDOMElement.addEventListener('ended', this._onVideoEnd);
    }
    updateVideo(initialRender) {
        if ((this._isPlaying || this._animator.isPlaying) && !initialRender) {
            return;
        }
        for (const videoSetting in this._settings) {
            const setting = videoSetting;
            if (!initialRender &&
                deepEqual(this._settings[setting], this._getElementSettingValue(setting))) {
                continue;
            }
            const settingValue = this._getElementSettingValue(setting);
            this._settings[setting] = cloneDeep(settingValue);
            /**
             * The autoplay attribute always has to be unset as it instead
             * has to be controlled from a timeline standpoint through
             * the animator events
             **/
            if (setting === 'autoplay') {
                continue;
            }
            switch (setting) {
                case 'startTime':
                case 'endTime':
                    this._setEffectiveVideoDuration();
                    this._resetTimings();
                    break;
                case 'playbackRate':
                    this._setPlaybackRate();
                    this._setEffectiveVideoDuration();
                    this._resetTimings();
                    break;
                case 'url':
                    this._setVideoSrc();
                    break;
                case 'sizeMode':
                    this._setSizeMode();
                    break;
                case 'playbackButton':
                    this._setPlaybackButton();
                    break;
                default:
                    this._videoDOMElement[setting] = settingValue;
                    break;
            }
        }
    }
    overrideUrl(url) {
        this._urlOverride = url.trim();
        this._setVideoSrc();
    }
    _setEffectiveVideoDuration() {
        if (!this.duration_m) {
            this._effectiveVideoDuration = 0;
        }
        else {
            const { startTime = 0, endTime } = this._settings;
            // Timeline seconds (rate-scaled, capped at the element duration)
            this._effectiveVideoDuration = getEffectiveDuration({
                elementDuration: this._viewElement.duration,
                assetDuration: this.duration_m,
                settingsStartTime: startTime,
                settingsEndTime: endTime,
                playbackRate: this._playbackRate
            });
            // Media seconds (trim length within the asset)
            this._effectiveVideoAssetDuration = getEffectiveVideoAssetDuration({
                elementDuration: this._viewElement.duration,
                assetDuration: this.duration_m,
                settingsStartTime: startTime,
                settingsEndTime: endTime
            });
        }
    }
    _setMuted(video) {
        video.muted = isMediaDefaultMuted(this._creativeConfig);
    }
    _setPlaybackButton() {
        const { color, size, enabled } = this._settings.playbackButton;
        const style = this._videoPlaybackButtons.style;
        style.display = enabled || size === 0 ? 'block' : 'none';
        style.setProperty(PlaybackButtonVariable.Color, color.toString());
        style.setProperty(PlaybackButtonVariable.Size, String(size / 100));
        this._setPlaybackButtonState();
    }
    _setPlaybackButtonState() {
        const style = this._videoPlaybackButtons.style;
        const playButtonVisible = this._isPlaying ? 'none' : 'block';
        const pauseButtonVisible = !this._isPlaying ? 'none' : 'block';
        style.setProperty(PlaybackButtonVariable.PlayState, playButtonVisible);
        style.setProperty(PlaybackButtonVariable.PauseState, pauseButtonVisible);
    }
    _getVideoUrl() {
        var _a, _b, _c;
        if (this._urlOverride !== undefined) {
            return this._urlOverride;
        }
        if (this._dataElement.feed) {
            const feedValue = (_a = this._feedStore) === null || _a === void 0 ? void 0 : _a.getFeedValueUrl(this._dataElement.feed, this._dataElement.id, this._dataElement);
            return (feedValue !== null && feedValue !== void 0 ? feedValue : '').trim();
        }
        const assetUrl = (_b = this._dataElement.videoAsset) === null || _b === void 0 ? void 0 : _b.url;
        if (!assetUrl) {
            if ((_c = this._dataElement.videoAsset) === null || _c === void 0 ? void 0 : _c.__loading) {
                return '';
            }
            throw new VideoError('Video asset URL is not defined.');
        }
        const customOrigin = this._creativeConfig.CUSTOM_AD_DOMAIN;
        if (customOrigin) {
            return replaceOrigin(assetUrl, customOrigin).trim();
        }
        return assetUrl
            .trim()
            .replace('http://storage-emulator:10000', this._creativeConfig.origins.VIDEOS_STORAGE);
    }
    _getElementSettingValue(setting) {
        if (setting === 'url') {
            return this._getVideoUrl();
        }
        return this._dataElement.videoSettings[setting];
    }
    /**
     * The media load algorithm resets playbackRate to defaultPlaybackRate on every load, so both
     * have to be set for the rate to survive the initial render and any later source change.
     */
    _setPlaybackRate() {
        this._videoDOMElement.defaultPlaybackRate = this._playbackRate;
        this._videoDOMElement.playbackRate = this._playbackRate;
    }
    _setSizeMode() {
        const mode = this._dataElement.videoSettings.sizeMode;
        switch (mode) {
            case VideoSizeMode.Fit:
                this._videoDOMElement.classList.remove('fillMode');
                break;
            case VideoSizeMode.Fill:
                this._videoDOMElement.classList.add('fillMode');
                break;
        }
    }
    async _setOptimizedVideoSrc(video) {
        var _a;
        if (!this._dataElement.videoUrls) {
            throw new VideoError('Video URLs not provided.');
        }
        try {
            const isStreamingEnabled = this._isStreamingEnabledInUrlParams();
            const { apple, standard } = this._dataElement.videoUrls.streamed;
            if (isStreamingEnabled && isAppleBrowser && isHlsSupported(video) && apple) {
                this._streamManager = new StreamManager(this._videoDOMElement, apple);
                this._videoDOMElement.src = await this._streamManager.loadStreams_m();
                this._streamingMode = 'hls';
                return;
            }
            if (isStreamingEnabled && standard && (await isAV1CodecDecodingSupported())) {
                this._streamManager = new StreamManager(this._videoDOMElement, standard);
                this._videoDOMElement.src = await this._streamManager.loadStreams_m();
                return;
            }
        }
        catch (e) {
            delete this._streamManager;
            this._streamingMode = undefined;
            const error = e;
            console.error(`${ErrorMessage.VideoSourceInitFailed}: ${error.message}. Falling back to non-streamed`);
        }
        const { apple, standard, fallback } = this._dataElement.videoUrls.nonStreamed;
        let videoUrl;
        if (isH265HVC1Supported(this._videoDOMElement) && apple) {
            videoUrl = apple; // This will either happen when the device can handle h265 but not streaming, or if the video is transparent (transparent videos aren't streamed)
        }
        else if (isWebmVp9Supported(this._videoDOMElement) && standard) {
            videoUrl = standard; // This will happen if the video is transparent (transparent videos aren't streamed)
        }
        else if (fallback) {
            videoUrl = fallback; // This will happen if the device is too old to handle anything more modern
        }
        else {
            videoUrl = (_a = this._dataElement.videoAsset) === null || _a === void 0 ? void 0 : _a.url;
        }
        if (!videoUrl) {
            throw new VideoError('No valid video url found');
        }
        if (this._videoDOMElement.src === videoUrl) {
            return;
        }
        this._videoDOMElement.src = videoUrl;
        this._videoDOMElement.load();
    }
    _setVideoSrc() {
        if (this._urlOverride !== undefined) {
            this._videoDOMElement.src = this._urlOverride;
            this._videoDOMElement.load();
            return;
        }
        if (this._dataElement.videoUrls && !this._dataElement.feed) {
            this._setOptimizedVideoSrc(this._videoDOMElement);
            return;
        }
        const url = this._getVideoUrl();
        if (this._videoDOMElement.src === url || !this._isSrcValid(url)) {
            return;
        }
        this._videoDOMElement.src = url;
        this._videoDOMElement.load();
    }
    _isSrcValid(url) {
        if (!url) {
            return false;
        }
        if (isRelativeUrl(url)) {
            return true;
        }
        const { pathname, hostname, href: src } = new URL(url);
        const isStorageEmulatorUrl = hostname === 'storage-emulator';
        const isBase64VideoUrl = isBase64Mp4Video(pathname);
        return !(isStorageEmulatorUrl && isValidUrl(src) && isBase64VideoUrl);
    }
    _resetTimings() {
        this._setVideoStartTime();
        this._setEndTime();
        this._videoOffset = 0;
        this._nonLoopVideoEnded = false;
    }
    _setLoopOffset() {
        if (this._settings.restartWithCreative) {
            this._videoOffset = 0;
            return;
        }
        if (this._settings.loop) {
            this._videoOffset = getNextOffset({
                elementDuration: this._dataElement.duration,
                effectiveAssetDuration: this._effectiveVideoAssetDuration,
                videoOffset: this._videoOffset,
                playbackRate: this._playbackRate
            });
            return;
        }
        // For non-looped videos, only calculate offset if video asset is longer than element
        // AND the video has not ended yet
        if (this._effectiveVideoAssetDuration / this._playbackRate > this._viewElement.duration &&
            !this._nonLoopVideoEnded) {
            const nextOffset = getNextOffset({
                elementDuration: this._dataElement.duration,
                effectiveAssetDuration: this._effectiveVideoAssetDuration,
                videoOffset: this._videoOffset,
                playbackRate: this._playbackRate
            });
            this._videoOffset = nextOffset;
            return;
        }
        this._videoOffset = 0;
    }
    _setOffsetTimings() {
        const startTime = this._settings.startTime + this._videoOffset;
        this._videoDOMElement.currentTime = startTime;
        const remainingVideoDuration = (this._effectiveVideoAssetDuration - this._videoOffset) / this._playbackRate;
        this._endTime = this._progressTime + remainingVideoDuration;
    }
    _setVideoStartTime() {
        this._videoDOMElement.currentTime = this._startTime;
    }
    _setEndTime() {
        this._endTime = getVideoEndTime({
            progressTime: this._progressTime,
            effectiveAssetDuration: this._effectiveVideoAssetDuration,
            playbackRate: this._playbackRate
        });
    }
    _setLoopEndTime() {
        this._endTime = this._progressTime + this._effectiveVideoAssetDuration / this._playbackRate;
    }
    _setLoopVideoStartTime() {
        this._videoDOMElement.currentTime = this._settings.startTime;
    }
    _showVideoLastFrame() {
        if (this._creativeConfig.environment === 0 /* AdEnvironment.LiveAd */) {
            this._videoDOMElement.currentTime = this._effectiveVideoAssetDuration;
            return;
        }
        if (this._settings.endTime) {
            this._videoDOMElement.currentTime = this._settings.endTime;
            return;
        }
        this._videoDOMElement.currentTime = this.duration_m;
    }
    async destroy() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        (_a = this._animator) === null || _a === void 0 ? void 0 : _a.off('play', this._onAnimatorPlay);
        (_b = this._animator) === null || _b === void 0 ? void 0 : _b.off('pause', this._pause);
        (_c = this._animator) === null || _c === void 0 ? void 0 : _c.off('stop', this._stop);
        (_d = this._animator) === null || _d === void 0 ? void 0 : _d.off('loop', this._onAnimationLoop);
        (_e = this._animator) === null || _e === void 0 ? void 0 : _e.off('animation_start', this._onAnimationStart);
        (_f = this._animator) === null || _f === void 0 ? void 0 : _f.off('animation_end', this._onAnimationEnd);
        (_g = this._animator) === null || _g === void 0 ? void 0 : _g.off('seek', this._onAnimatorSeek);
        (_h = this._animator) === null || _h === void 0 ? void 0 : _h.off('isPlaying', this._onAnimatorIsPlaying);
        (_j = this._renderer) === null || _j === void 0 ? void 0 : _j.off('mute', this._onRendererMute);
        (_k = this._renderer) === null || _k === void 0 ? void 0 : _k.off('creativeAudioToggled', this._onCreativeAudioSettingToggled);
        this._videoDOMElement.removeEventListener('ended', this._onVideoEnd);
        cancelAnimationFrame(this._requestId);
        clearInterval(this._previewLoopIntervalId);
        (_l = this._streamManager) === null || _l === void 0 ? void 0 : _l.destroy_m();
        delete this._streamManager;
        this._videoDOMElement.pause();
        this._videoDOMElement.removeAttribute('src');
        this._videoDOMElement.load();
        this._destroyed = true;
        destroyDiInstance(this);
        this._creativeAudioToggle.destroy_m();
        await removeCreativeAudioToggleIfUnused(this._renderer);
    }
}
var PlaybackButtonVariable;
(function (PlaybackButtonVariable) {
    PlaybackButtonVariable["Color"] = "--playbackButtonColor";
    PlaybackButtonVariable["Size"] = "--playbackButtonSize";
    PlaybackButtonVariable["PlayState"] = "--playbackButtonPlayState";
    PlaybackButtonVariable["PauseState"] = "--playbackButtonPauseState";
})(PlaybackButtonVariable || (PlaybackButtonVariable = {}));
